import type { OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { EmptyArray } from 'lib/array/arrayUtils'

/**
 * Resolves the opt-in bundle a room has selected against the bundles its *current* rate actually
 * offers. State stores only the bundle id, so a selection that no longer matches the rate on screen (a
 * race between a rate refresh and a reducer reset) resolves to nothing rather than to a price the
 * shopper can no longer see.
 *
 */
export function resolveSelectedOptionalBundles(
  rate: App.EnrichedPropertyRate | undefined,
  selectedBundleIds: Array<string> | undefined,
): Array<App.Property.OptionalBundle> {
  if (!rate || !selectedBundleIds?.length) return EmptyArray
  const selectedBundles = (rate.optionalBundles ?? []).filter(({ bundleId }) => selectedBundleIds.includes(bundleId))
  return selectedBundles.length ? selectedBundles : EmptyArray
}

/** Total a room's selected bundles add on top of its hotel rate. Display-time only. */
export function getSelectedOptionalBundlesPrice(
  rate: App.EnrichedPropertyRate | undefined,
  selectedBundleIds: Array<string> | undefined,
): number {
  let total = 0
  for (const optionalBundle of resolveSelectedOptionalBundles(rate, selectedBundleIds)) {
    total += optionalBundle.totalPrice
  }
  return total
}

/** A travel product selected for any room qualifies every hotel's policy in the booking. */
export function hasSelectedPropertyTravelProducts(
  rooms: ReadonlyArray<OfferPageRoom>,
  ratesByRoom: Readonly<Record<string, App.EnrichedPropertyRate | undefined>>,
  excludingRoomId?: string,
): boolean {
  return rooms.some(room => {
    if (room.id === excludingRoomId) return false
    const rate = ratesByRoom[room.id]
    return !!rate && (rate.includesFlights || !!rate.bundle ||
      resolveSelectedOptionalBundles(rate, room.optionalBundleIds).length > 0)
  })
}
